import React from 'react'
import { NextSeo } from 'next-seo'
import { ExternalLink } from '@/atoms/ExternalLink'
import { InternalLink } from '@/atoms/InternalLink'
import { PaddedContainer } from '@/atoms/PaddedContainer'
import { paths } from '@/constants/paths'
import { LegalLayout } from '@/layouts/LegalLayout'
import { NextPageWithLayout } from '@/layouts/types'
import { getStaticPropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { getGenericStaticPaths } from '@/utils/nextUtils/staticPages'
import { loadTranslations } from '@/utils/translate/translate-server'

const ArchivedTermsOfUsePage: NextPageWithLayout = () => {
  return (
    <>
      <NextSeo nofollow noindex />
      <TermsOfUse_2024_02_20 />
    </>
  )
}

ArchivedTermsOfUsePage.getLayout = (page) => {
  return <LegalLayout>{page}</LegalLayout>
}

export const getStaticProps = getStaticPropsErrorHandler(
  { filePath: 'pages/[locale]/legal/terms-of-use/2024-02-20.tsx' },
  async ({ locale }) => {
    return {
      props: {
        ...(await loadTranslations(locale, ['common', 'account'])),
      },
      revalidate: false,
    }
  },
)

export const getStaticPaths = getGenericStaticPaths

export default ArchivedTermsOfUsePage

const orderedListRoman = 'list-[lower-roman] list-inside ml-4'
const orderedListAlpha = 'list-[lower-alpha] list-inside ml-4'

/**
 *
 * Hey, are you about to make changes to this file? Read this!!!
 *
 * When the Terms of Use gets an update, the following process needs to be followed:
 *   - Copy this file and make a new file.
 *   - Name the new file after the date of the udpate using the pattern YYYY-MM-DD.
 *   - Make your changes in the new file. Don't change this copy! It's an official archive!
 *   - Make sure to update the "Updated:" date in the new file.
 *   - Update the name of the exported component in the new file to TermsOfUse_YYYY_MM_DD
 *   - Import and use the new component in the "evergreen" terms of use file: ./index.tsx
 *
 * That should be it for the code changes. After you have deployed and verified that the new page
 * is up in production and that the main page is using the new version of the Terms of Use, then
 * you'll need to make changes in the auth service.
 *   - Go to https://auth.angel.com/admin/document
 *     - You will need an admin account for Ellis Island to access this. If you do not have access,
 *       then ask for help getting access in Slack, currently the #platform-dev channel.
 *   - Find the latest "Terms of Use" document for angel.com and take note of the version number.
 *     Note that there may be other "Terms of Use" documents for other websites, e.g. invest.angel.com.
 *   - Create a new "Document" using the "Add New Document" button.
 *   - The "Title" will be visible to the end user, so the title should be "Terms of Use".
 *   - Set the "Source" to the fully qualified url for the newly deployed terms page.
 *     For example, https://www.angel.com/legal/terms-of-use/2024-02-20
 *     It is important to use the full url, because it will get stored as the document which was signed.
 *     Do not use the evergreen url as the source.
 *   - Set the "Version" to one number higher than the previous document.
 *   - Check the "Published" boolean.
 *   - Click the "Create" button.
 *
 * Now that you've created a new document, you need to make it required in the sign in flow.
 *   - Go to https://auth.angel.com/admin/client
 *   - Find the "Angel Web" client application and click on it.
 *   - In the "Required Documents" section, de-select the old Terms of Use document and select the new one.
 *   - Scroll to the bottom of the page and click "Save Changes"
 *
 * Everything should be wired up now. Verify that it's working by signing out and then sign in again
 * on angel.com. When you sign in, you should be required to accept the new Terms of Use before signing in.
 *
 * After verifying that the Terms of Use has been updated and the flow is working for web, broadly publicize
 * the change. The other apps that rely on the web Terms of Use will need to update their client applications
 * as well in Ellis Island to require the new version of the Terms of Use in their sign in flows.
 *
 */

export const TermsOfUse_2024_02_20: React.FC = () => {
  return (
    <div className="bg-white py-20 text-justify text-black">
      <PaddedContainer>
        <div className="flex flex-col items-center gap-4 p-10">
          <h1 className="text-center text-[1.3rem] font-bold md:text-[2rem]">
            <p data-testid="company">Angel Studios</p>
            <p data-testid="terms-of-use-header">TERMS OF USE</p>
          </h1>
          <p>Updated: February 20, 2024</p>

          <p>
            Welcome! Angel Studios, Inc. (“Angel Studios” or “we”, “us”, or “our”) offers a cost-free video streaming
            service (our “Service”) that allows our audience members to watch streamed motion pictures and television
            (the “Content”) through our websites, including, but not limited to,{' '}
            <InternalLink data-testid="home-link" className="underline" href={paths.home}>
              {paths.getBaseURL()}
            </InternalLink>
            , along with the features, functionalities, browser extensions and other services available through our
            websites (collectively, our “Websites”) and our web applications, including, but not limited to, Angel
            Studios Android App, Angel Studios iOS App, The Chosen Android App, The Chosen iOS App, Angel Studios Roku
            App, Angel Studios Google TV App, Angel Studios tvOS App, available on the Apple App Store, Google Play
            Store, Roku Store, Google TV Store, FireTV App Store,and the app stores for all other Angel Studios
            Supported Devices (collectively, our “Apps”) (our Service, Websites and Apps are referred to herein as our
            “Platform”). See our{' '}
            <InternalLink data-testid="devices-link" className="underline" href={paths.legal.devices}>
              Supported Devices
            </InternalLink>{' '}
            page for a full list of the devices we support. Angel Studios also offers monthly and annual memberships to
            its Angel Guild for a fee, which allows Guild Members the opportunity to vote on new film and television
            projects and to receive other membership benefits, including by way of example and not limitation, two (2)
            complimentary tickets to every Angel theatrical release during the term of your Guild Membership. Only the
            Guild Membership requires payment; the Service may be accessed without payment of any kind, subject to the
            Terms of Use described herein below.
          </p>

          <p>
            These Terms of Use (“Terms”) apply to our Platform, including, but not limited to, our Websites and Apps
            that link to these Terms and any related services, applications or platforms where these Terms appear or are
            linked. Please note, these Terms include hyperlinks that you may only be able to access through a web
            browser which may not be available on the particular Angel Studios Supported Device you are using to review
            these Terms. Accordingly, you may need to visit{' '}
            <InternalLink data-testid="legal-link" className="underline" href={paths.legal.index}>
              {paths.getBaseURL()}
              {paths.legal.index}
            </InternalLink>{' '}
            from a web browser to fully review these Terms.
          </p>

          <p>
            By accessing or using our Platform in any way, including, without limitation, accessing any portion of our
            Platform or submitting anything to us through or in connection with our Platform, you agree to read, comply
            with, and be legally bound by: (1) these Terms; (2) any additional terms and conditions agreements, and
            policies published on our Platform or otherwise made available to you that are applicable to your use of our
            Platform (the “Rules”); and (3) any other agreements applicable to your access to or use of our Platform
            (the “Additional Agreements”). These Terms, the Rules, and any and all Additional Agreements are
            collectively referred to in these Terms as the “Agreements”.
          </p>

          <p className="font-semibold">
            REVIEW THESE TERMS CAREFULLY. BY ACCESSING OR USING OUR PLATFORM (IN WHOLE OR IN PART), YOU AGREE TO BE
            BOUND BY THESE TERMS. IF YOU DO NOT AGREE WITH THESE TERMS YOU ARE NOT ELIGIBLE TO ACCESS OR US OUR PLATFORM
            (IN WHOLE OR IN PART). FURTHER, IF YOU ARE NOT ELIGIBLE TO USE OUR PLATFORM PURSUANT TO ANY OF THE OTHER
            AGREEMENTS, YOU ARE NOT PERMITTED TO ACCESS OR USE OUR PLATFORM AS PROHIBITED UNDER SUCH OTHER AGREEMENTS.
          </p>
          <p className="font-semibold">
            THESE TERMS REQUIRE THE USE OF ARBITRATION ON AN INDIVIDUAL BASIS TO RESOLVE DISPUTES, RATHER THAN COURT OR
            JURY TRIALS OR CLASS ACTIONS AND ALSO LIMIT THE REMEDIES AVAILABLE TO YOU IN THE EVENT OF A DISPUTE.
            CAREFULLY REVIEW THE DISPUTE RESOLUTION SECTION OF THESE TERMS BEFORE YOU ACCESS OR USE OUR PLATFORM.
          </p>
          <p className="font-semibold">
            PLEASE NOTE, OTHER THAN THE SERVICE AND THE GUILD MEMBERSHIP, THESE TERMS DO NOT APPLY TO ANY OTHER SERVICES
            OR OTHER ACTIVITIES OPERATED OR OTHERWISE MADE AVAILABLE BY ANGEL STUDIOS OR ITS AFFILIATES, INCLUDING,
            WITHOUT LIMITATION, THE ANGEL MARKETPLACE, WHICH IS GOVERNED BY THE ANGEL MARKETPLACE TERMS OF SERVICE,
            AVAILABLE AT{' '}
            <InternalLink
              data-testid="market-terms-link"
              className="underline"
              href={paths.legal.marketplaceTermsOfService}
            >
              {paths.getBaseURL()}
              {paths.legal.marketplaceTermsOfService}
            </InternalLink>
            , OR THE ANGEL FUNDING WEBSITE OPERATED BY OUR AFFILIATE, VAS PORTAL, LLC, WHICH IS GOVERNED BY THE ANGEL
            FUNDING TERMS OF SERVICE, AVAILABLE AT{' '}
            <ExternalLink
              data-testid="invest-terms-link"
              className="underline"
              href={paths.external.invest.termsOfService}
            >
              {paths.external.invest.termsOfService}
            </ExternalLink>
            .
          </p>

          <ol className="list-inside list-decimal">
            <li className="mt-4">
              <span className="font-semibold">ACCEPTANCE AND APPLICABILITY OF AGREEMENTS</span>
              <ol className={orderedListRoman}>
                <li className="mt-4">
                  <span className="font-semibold">Acceptance of Agreements.</span> By accessing or using our Platform
                  you agree to be bound by these Terms, whether you access or use our Platform as a visitor (meaning
                  that you browse our Platform without creating an account) or as a registered user (meaning that you
                  have created an account with us). To use our Platform as a registered user, you must read and confirm
                  that you accept these Terms and any other applicable Agreements.
                </li>
                <li className="mt-4">
                  <span className="font-semibold">Eligibility Representations and Warranties.</span> Without limiting
                  anything set forth in the Agreements, our Platform is intended for use solely by individuals who are
                  at least 18 years of age (or age of majority if higher in your place of residence) and are legally
                  permitted to engage in the activities available through our Platform. To that end, in addition to
                  anything set forth in the Agreements, by accessing and using our Platform, you represent to us that:
                  (1) you are 18 years of age or older (or age of majority or older if higher in your place of
                  residence); (2) you are legally able to enter into contracts; (3) you are not a person barred from
                  receiving or using our Platform under federal, state, local or other laws; and (4) you have not
                  previously been suspended or prohibited from using our Platform for any reason other than your
                  cancellation of your account on our Platform.
                </li>
                <li className="mt-4">
                  <span className="font-semibold">Changes to Terms.</span> We may update or modify these Terms from time
                  to time, with or without providing notice directly to you. We may do so without limitation by updating
                  these Terms on our Sites and by publishing a general notice of the update on our Sites. Where required
                  by applicable law, we will provide you with notice of any such changes, either through your account,
                  by email, or other communication methods permitted by law or the Agreements. You can always review the
                  most current version of these Terms by clicking the “Terms of Service” link on our Websites and Apps.
                  By accessing or using our Platform after we have provided notice of any changes to the Terms, you
                  agree to be bound by such modifications.
                </li>
                <li className="mt-4">
                  <span className="font-semibold">Our Rights.</span> We have the right, but not the obligation, to take
                  any of the following actions in our sole discretion without providing any prior notice to you and
                  without liability to you or any third party:
                  <ol className={orderedListAlpha}>
                    <li className="mt-4">change or terminate all or any part of our Platform;</li>
                    <li className="mt-4">restrict or terminate your access to all or any part of our Platform;</li>
                    <li className="mt-4">refuse, move, or remove anything that is available on our Platform; or</li>
                    <li className="mt-4">
                      deny access to our Platform to anyone at any time in our sole and absolute discretion.
                    </li>
                  </ol>
                </li>
                <li className="mt-4">
                  <span className="font-semibold">Conflicts.</span> In the event of any conflicts between these Terms
                  and any other Agreement, unless otherwise specified in such other Agreement, these Terms shall
                  control.
                </li>
              </ol>
            </li>

            <li className="mt-4">
              <span className="font-semibold">ACCEPTABLE USE OF OUR PLATFORM</span>
              <ol className={orderedListRoman}>
                <li className="mt-4">
                  <span className="font-semibold">Acceptable Use.</span> Your access to and use of our Platform must
                  comply with the following (the “Acceptable Use Restrictions”):
                  <ol className={orderedListAlpha}>
                    <li className="mt-4">
                      You are only allowed to access and use our Platform for its intended purposes, as determined by us
                      in our sole discretion.
                    </li>
                    <li className="mt-4">
                      Without limitation, you are not allowed to access or use our Platform to: (1) publish, post,
                      distribute or disseminate any content which is or could reasonably be viewed as: (a) hate speech,
                      obscene, harassing, threatening, pornographic, abusive or defamatory to an individual or group of
                      individuals on the basis of religious belief, race, gender, age, disability or otherwise; (b)
                      inciting violence, or containing graphic or gratuitous violence; (c) an unauthorized commercial
                      communication of any kind (including, without limitation, spam); (d) fraudulent, inaccurate or
                      misleading, or otherwise objectionable content of any kind; (e) infringing or violating someone
                      else’s rights or otherwise violating the law; or (f) identifying any person without their consent
                      or disclosing anyone else’s personal contact details or invading their privacy; (2) violate any
                      laws; (3) transmit or upload any software or other materials that contain any viruses, worms,
                      trojan horses, defects, time bombs or other items of a destructive nature; or (4) engage in
                      commercial activity except as expressly permitted under the applicable Agreements.
                    </li>
                    <li className="mt-4">
                      You are also prohibited from: (1) reformatting or framing any portion of our Platform; (2) using
                      any device, software, or procedure that interferes with, or attempts to interfere with, the normal
                      operation of our Platform; (3) taking any action that imposes, or may impose, as determined by us
                      in our sole discretion, an unreasonable or disproportionately large load on our information
                      technology infrastructure; (4) modifying, adapting, translating, disassembling, decompiling,
                      translating or reverse engineering any portion of our Platform or otherwise attempting to
                      reconstruct or discover any source code or underlying ideas, algorithms, file formats, or
                      programming interoperability interfaces of our Platform; (5) disrupting or otherwise interfering
                      with our Platform or the networks or servers we use; (6) impersonating any person or entity or
                      misrepresenting your connection or affiliation with a person or entity; (7) collecting or storing,
                      or attempting to collect or store, personal information about other users of our Platform, except
                      as expressly permitted under the applicable Agreements; (8) engaging in any activity that is
                      illegal under federal, state, local, or other laws; (9) creating a false identity on our Platform;
                      (10) creating an account on our Platform for anyone else; (11) releasing to any third party
                      information related to your access to or use of our Platform for purposes of monitoring our
                      Platform’s availability, performance, or functionality, or for any other benchmarking or
                      competitive purposes without our prior written approval; (12) copying our Platform, except as
                      expressly permitted under the applicable Agreements; (13) accessing or using our Platform in a
                      service bureau or time-sharing environment (including, without limitation, accessing our Platform
                      to provide third parties a service consisting solely of the collection and entry of data and other
                      information on our Platform); (14) selling, assigning, transferring, sublicensing, pledging,
                      renting, or otherwise sharing your rights under the Agreements; (15) creating any derivative works
                      based on our Platform; and (16) modifying, obscuring, or removing any proprietary notices on our
                      Platform or copies thereof.
                    </li>
                  </ol>
                </li>
                <li className="mt-4">
                  <span className="font-semibold">Access and Use.</span>
                  <ol className={orderedListAlpha}>
                    <li className="mt-4">
                      To access the Content through our Platform, you must use a computer, mobile device, streaming
                      media player or other device that meets our system and compatibility requirements (each, an “Angel
                      Studios Supported Device”). You can find all supported devices at{' '}
                      <InternalLink data-testid="devices-link" className="underline" href={paths.legal.devices}>
                        {paths.getBaseURL()}
                        {paths.legal.devices}
                      </InternalLink>
                      . The Apps we use to deliver the Content may vary across the Angel Studios Supported Devices and
                      the features and functionalities available through our Apps may differ. FOR EACH AND EVERY APP YOU
                      INSTALL ON YOUR ANGEL STUDIOS SUPPORTED DEVICE(S), YOU ACKNOWLEDGE THAT WE MAY UPDATE SUCH APPS
                      FROM TIME TO TIME, WHICH MAY AFFECT THE FEATURES AND FUNCTIONALITIES OF EACH SUCH APP. FURTHER,
                      YOU AGREE TO RECEIVE, WITHOUT FURTHER NOTICE OR PROMPTING, UPDATED VERSIONS OF ANY AND ALL APPS
                      YOU HAVE INSTALLED ON YOUR ANGEL STUDIOS SUPPORTED DEVICE(S) THAT WE PROVIDE FROM TIME TO TIME. IF
                      YOU DO NOT AGREE TO RECEIVE SUCH UPDATED APP(S), DO NOT USE THE APPLICABLE APP.
                    </li>
                    <li className="mt-4">
                      Access to and use of our Platform requires access to the Internet. You are responsible for
                      providing all equipment necessary to establish a connection to the Internet, and any telephone,
                      wireless, or other connection and service fees associated with such access. Using our Platform may
                      allow you to receive content on your mobile phone or wireless device. The manner in which that
                      content is delivered to your mobile phone or wireless device may cause you to incur extra data,
                      text messaging, or other charges from your wireless carrier, which are your sole responsibility.
                    </li>
                  </ol>
                </li>
                <li className="mt-4">
                  <span className="font-semibold">Compliance with Applicable Laws.</span> You certify that you will
                  comply with all applicable laws (e.g., federal, state, local, and other laws) and the applicable
                  Agreements when accessing or using our Platform. Without limiting the foregoing, by accessing or using
                  our Platform, you represent and warrant that: (1) you are not located in a country that is subject to
                  a U.S. Government embargo or that has been designated by the U.S. Government as a “terrorist
                  supporting” country; and (2) you are not listed on any U.S. Government list or prohibited or
                  restricted parties. If you access or use our Platform outside the United States, you are solely
                  responsible for ensuring that your access to and use of the Platform in such country, territory, or
                  jurisdiction does not violate any applicable laws. We reserve the right, but have no obligation to, in
                  our sole discretion, monitor where our Platform is accessed from, and the right, but not the
                  obligation, to block or otherwise restrict access to our Platform, in whole or in part, from any
                  geographic location.
                </li>
                <li className="mt-4">
                  <span className="font-semibold">Mistakes or Errors.</span> Without limiting anything set forth in the
                  applicable Agreements, you acknowledge and agree that under no circumstances will we or any of our
                  licensors or suppliers be responsible for any loss, damage, or liability arising out of any mistakes
                  or other errors made by you as a result of your access to or use of our Platform.
                </li>
              </ol>
            </li>

            <li className="mt-4">
              <span className="font-semibold">ACCOUNTS</span>
              <ol className={orderedListRoman}>
                <li className="mt-4">
                  <span className="font-semibold">Registration.</span> You may be required to open an account or
                  register with us in order to use some of the features on the Platform, including, but not limited to,
                  the Services. When registering for an account, you must provide true, accurate, current and complete
                  information about yourself as may be prompted by any registration form. If any information you
                  previously provided to us changes, you must promptly update the relevant information.
                </li>
                <li className="mt-4">
                  <span className="font-semibold">Third Party Logins.</span> Our Platform may, now or in the future,
                  provide you with the ability to register and sign in through services provided by certain third
                  parties that you already utilize, such as Google or Facebook. In these instances, you will use your
                  account credentials associated with the applicable third party service to login to your account on our
                  Platform. In addition to the applicable Agreements, your use of any third party login service must
                  also comply with all applicable terms and other agreements between you and the third party related to
                  your use of your account credentials on other websites and online services. Your ability to use this
                  option may be limited where the applicable third party requires you to use a device or operating
                  system that meet that third party’s requirements. Additionally, by using this option, you acknowledge
                  that you understand the applicable third party will know that you are logging into our Platform and
                  that you are a user of our Platform.
                </li>
                <li className="mt-4">
                  <span className="font-semibold">Account Security and Responsibility.</span> You are responsible for
                  maintaining the confidentiality of your password and account and are fully responsible for all
                  activities that occur under your password and account. You agree to: (1) notify us immediately of any
                  unauthorized use of your account or any breach of the security of your account; and (2) ensure that
                  you fully logout of your account at the end of each session. You must set up your account in your own
                  name (except when, and only to the extent, you create one or more sub-accounts on our Platform for
                  your Authorized Users). You further agree not to access or use anyone else’s account on our Platform.
                  Angel Studios cannot and will not be liable for any loss or damage arising from your failure to comply
                  with this provision or any of the applicable Agreements. If we suspend or terminate your account, you
                  acknowledge that all information and content associated with such account will no longer be available
                  to you.
                </li>
                <li className="mt-4">
                  <span className="font-semibold">Additional Information Sharing.</span> If you open an account, you
                  understand that we will collect and maintain information related to your access to and use of our
                  Platform. We connect, though may not always display, that information to your account in order to
                  conduct our business operations.
                </li>
                <li className="mt-4">
                  <span className="font-semibold">Our Termination or Suspension of Your Account.</span> Without limiting
                  any other rights we have under the applicable Agreements, in the event we determine, in our sole
                  discretion, that your account or information associated with your account may be compromised due to
                  your personal device being infected with a virus, malware, other malicious code, or due to other theft
                  of your account login credentials or other account information, we reserve the right, but do not have
                  the obligation, to invalidate, delete, or otherwise modify your account and the associated account
                  information in order to protect your account, the accounts of other users, and our systems from
                  further damage or exposure. This may include proactively changing your password. If we need to do
                  this, we will make reasonable efforts to inform you of any modifications made via the email address
                  listed on your account or other viable means of communication.
                </li>
                <li className="mt-4">
                  <span className="font-semibold">Account Cancellation.</span> Subject to restrictions and other
                  obligations set forth in the applicable Agreements, you may close your account by contacting us at{' '}
                  <a className="underline" href="mailto:support@angel.com">
                    support@angel.com
                  </a>{' '}
                  and following the instructions we provide. If you cancel your account, these Terms will be terminated
                  and your license to access and use our Platform shall immediately terminate and you must discontinue
                  your access to and use of our Platform. Please note, in some circumstances, we may not permit you to
                  close your account, including in situations where: (1) you are not permitted to close your account
                  pursuant to the terms of an applicable Agreement; (2) you have an open dispute or claim; (3) if your
                  account is subject to a hold; and/or (4) you are cancelling your account in an attempt to evade any
                  investigation.
                </li>
              </ol>
            </li>

            <li className="mt-4">
              <span className="font-semibold">CONTENT</span>
              <ol className={orderedListRoman}>
                <li className="mt-4">
                  <span className="font-semibold">Available Content.</span> Angel Studios provides TV series and films
                  that amplify light.
                </li>
                <li className="mt-4">
                  <span className="font-semibold">Content Quality.</span> The time it takes to load and begin watching
                  the Content on your Angel Studios Supported Device(s), as well of the quality of the Content displayed
                  on your Angel Studios Supported Device(s), can vary from time to time, and may be affected by a
                  variety of factors, such as your location, the features and functionalities of a specific Angel
                  Studios Supported Device and the bandwidth available through, and/or the speed of, your internet
                  connection. HD, Ultra HD and HDR availability is subject to your internet service and Angel Studios
                  Supported Device capabilities. Not all Content is available in some formats, such as HD, Ultra HD or
                  HDR. Default playback settings on cellular networks exclude HD, Ultra HD and HDR Content. The minimum
                  connection speed for SD quality is 0.5 Mbps; however, we recommend a faster connection for improved
                  video quality. A download speed of at least 5.0 Mbps per stream is recommended to receive HD Content
                  (defined as a resolution of 720p or higher). A download speed of at least 25.0 Mbps per stream is
                  recommended to receive Ultra HD Content (defined as a resolution of 1080p or higher) and HDR Content.
                  Angel Studios makes no representations or warranties about the quality of your watching experience on
                  your Angel Studios Supported Device(s).
                </li>
              </ol>
            </li>

            <li className="mt-4">
              <span className="font-semibold">PLATFORM OWNERSHIP AND LICENSE</span>
              <ol className={orderedListRoman}>
                <li className="mt-4">
                  <span className="font-semibold">Ownership.</span> All right, title and interest in and to our
                  Platform, including, but not limited to, the Content, all of the software and code that comprise and
                  operate our Platform, and all of the text, photographs, illustrations, images, graphics, audio, video,
                  URLs and other materials provided through our Platform are owned by us or third parties who have
                  licensed their content to us. Our Platform is protected under trademark, service mark, trade dress,
                  copyright, patent, trade secret and other intellectual property laws. In addition, the entire Platform
                  is a collective work under U.S. and international copyright laws and treaties, and we own the
                  copyright in the selection, coordination, arrangement and enhancement of our Platform.
                </li>
                <li className="mt-4">
                  <span className="font-semibold">Marks.</span> The Angel Studios names and logos (including, but not
                  limited to, those of its affiliates), all product and service names, all graphics, all button icons
                  (e.g., investor badges), and all trademarks, service marks, and logos appearing within our Platform,
                  unless otherwise noted, are trademarks (whether registered or not), service marks, and/or trade dress
                  of Angel Studios (collectively, “Our Marks”). All other trademarks, product names, company names,
                  logos, service marks, and/or trade dress (collectively, “Other Marks”) mentioned, displayed, cited, or
                  otherwise indicated within our Platform are the property of their respective owners. You are not
                  authorized to display or use Our Marks in any manner without our prior written permission. You
                  explicitly acknowledge and agree that Investor Badges are primarily for marketing purposes and are not
                  proof of ownership or investment in any Angel film or television series. You are not authorized to
                  display or use any Other Marks that appear on our Platform without the prior written permission of the
                  applicable third party.
                </li>
                <li className="mt-4">
                  <span className="font-semibold">Limited License.</span> Subject to your acceptance of, and compliance
                  with, the applicable Agreements, we hereby grant you a limited, non-exclusive, non-transferable,
                  non-sublicensable, revocable right to access and use our Platform in a manner that is consistent with
                  the applicable Agreements and our Platform’s intended purpose(s). You obtain no rights in our Platform
                  except to use it in accordance with the applicable Agreements. Without limiting the generality of the
                  foregoing, you shall not access or use our Platform in violation of the terms set forth in any of the
                  applicable Agreements, including, without limitation, the Acceptable Use Restrictions set forth in
                  these Terms (collectively, “Prohibited Activities”). You will be solely liable for any damages, costs
                  or expenses arising out of or in connection with your commission of any Prohibited Activity. You shall
                  notify us immediately upon becoming aware of the commission by any person of a Prohibited Activity and
                  shall provide us with reasonable assistance upon our request with any investigations we may conduct in
                  connection with any such Prohibited Activity.
                </li>
              </ol>
            </li>

            <li className="mt-4">
              <span className="font-semibold">USER CONTENT AND INFORMATION</span>
              <ol className={orderedListRoman}>
                <li className="mt-4">
                  <span className="font-semibold">User Content.</span> Our Platform may provide users with the ability
                  to add, create, upload, submit, distribute, post, or share content on or through our Platform,
                  including, but not limited to, website links, opinions, information, posts, profiles, pictures,
                  videos, and audio clips (collectively, “User Content”). Reliance on, and submission of, User Content,
                  including, but not limited to the use of the ‘record’ button or function on our apps and sites is at
                  your own risk. You acknowledge and agree that we are not responsible or liable for any User Content
                  that appears on our Platform.
                </li>
                <li className="mt-4">
                  <span className="font-semibold">Your Content.</span> If you provide any User Content (“Your Content”),
                  unless otherwise specified in an applicable Agreement, you expressly grant, and represent and warrant
                  that you have the right to grant, us a non-exclusive, irrevocable, worldwide, transferable,
                  royalty-free, perpetual license to publicly display, publicly perform, reproduce, distribute, create
                  derivative works of, and sublicense Your Content in any manner or through any media now known or later
                  developed without any payment obligation to you. Without limiting the foregoing, you acknowledge and
                  agree that Your Content may be viewable by any other users of our Platform (whether registered or
                  unregistered). Further, you understand and agree that we have the right, but not the duty, to
                  pre-screen, edit, refuse, move, or remove any User Content, including, without limitation, Your
                  Content, posted to our Platform. Profile pictures and story videos are not protected and may be
                  “scraped” by third-party URLs and websites without Angel’s knowledge or consent; accordingly, you
                  agree to release and indemnify Angel from any such third party usage of your User Content.
                </li>
                <li className="mt-4">
                  <span className="font-semibold">Private Messages.</span> The Platform may provide you with the ability
                  to send private messages to other users of the Platform (“Private Messages”). Private Messages are not
                  considered User Content and we will not intentionally make any such Private Messages available to any
                  party other than the intended recipient. However, you should remain mindful that there is an inherent
                  risk in the use of the internet. Private Messages may be intercepted without our knowledge or consent,
                  collected illegally, and/or forwarded by recipients to others. We cannot guarantee the security of any
                  information, including Your Private Messages, that you disclose online, and you do so at your own
                  risk.
                </li>
                <li className="mt-4">
                  <span className="font-semibold">Abusive and Offensive Language.</span> Abusive and offensive language
                  will not be tolerated on our Platform, social media sites, or with our personnel. You are not entitled
                  to make untrue, malicious, and/or damaging comments with regard to our operations in any media or
                  forum.
                </li>
                <li className="mt-4">
                  <span className="font-semibold">Personal Information.</span> By accessing or using our Platform, you
                  are consenting to the collection, use, disclosure, transfer, and sharing of your personal information,
                  including, but not limited to, sharing such information with companies other than Angel Studios as
                  described in our Privacy Policy, available at{' '}
                  <InternalLink
                    data-testid="privacy-policy-link"
                    className="underline"
                    href={paths.legal.privacyPolicy}
                  >
                    {paths.getBaseURL()}
                    {paths.legal.privacyPolicy}
                  </InternalLink>
                  . The Privacy Policy may be updated from time to time, so please review it regularly. If you do not
                  agree with the collection and use of personal information as described in the Privacy Policy, please
                  discontinue all access to and use of our Platform.
                </li>
                <li className="mt-4">
                  <span className="font-semibold">Text and Voice Communications.</span> We may, now or in the future,
                  provide you with the opportunity to receive promotional and personalized advertising text messages and
                  marketing and other phone calls made using an automatic telephone dialing system and/or an artificial
                  or prerecorded voice (“Angel Studios Messages”). By signing up to receive Angel Studios Messages, or
                  by otherwise indicating to us that you want to receive Angel Studios Message, such as by sending a
                  short code keyword text message from your mobile device in response to a promotion or offer we make
                  (“Angel Studios Messages Enrollment”), in addition to these Terms, you also agree to the following:
                  <ol className={orderedListAlpha}>
                    <li className="mt-4">
                      You expressly consent to receive text messages, marketing phone and other phone calls made using
                      an automatic telephone dialing system and/or an artificial or prerecorded voice from Angel Studios
                      on any mobile or residential phone number you provide to Angel Studios as part of your Angel
                      Studios Messages Enrollment.
                    </li>
                    <li className="mt-4">
                      You understand that consent to receive Angel Studios Messages is not required to make any purchase
                      and you may revoke your consent at any time.
                    </li>
                    <li className="mt-4">
                      You represent and warrant to us that you are the account holder of, or that you have the express
                      permission of the account holder for, any mobile or residential phone number you provide us as
                      part of your Angel Studios Messages Enrollment. You further agree to promptly notify us if you are
                      no longer the account holder of, or you no longer have the express permission of the account
                      holder for, any mobile or residential phone number you previously provided us in connection with
                      your Angel Studios Messages Enrollment.
                    </li>
                    <li className="mt-4">
                      You must be 18 years of age or older to provide consent to receive Angel Studios Messages.
                    </li>
                    <li className="mt-4">
                      You understand that message and data rates may apply and that you, not Angel Studios, will be
                      solely responsible for any carrier charges imposed on you due to your receipt of any text messages
                      or phone calls, including, without limitation, any Angel Studios Messages.
                    </li>
                    <li className="mt-4">
                      You agree not to send or otherwise make any content available in connection with the Angel Studios
                      Messages that would otherwise violate the applicable Agreements.
                    </li>
                  </ol>
                  <p className="mt-4">
                    You can reply HELP to any text Angel Studios Messages you receive for assistance. Additionally, you
                    can email us at{' '}
                    <a data-testid="support-link" className="underline" href="mailto:support@angel.com">
                      support@angel.com
                    </a>
                    .
                  </p>
                  <p className="mt-4">
                    You can withdraw your consent to receive Angel Studios Messages at any time by replying STOP to any
                    text Angel Studios Messages you receive. You can also withdraw your consent by sending us an email
                    at{' '}
                    <a data-testid="support-link" className="underline" href="mailto:support@angel.com">
                      support@angel.com
                    </a>{' '}
                    and including your phone number and the subject line “STOP” in the email. If you withdraw your
                    consent, we may send you one final text message to confirm withdrawal of your consent.
                  </p>
                  <p className="mt-4">
                    We reserve the right to change the terms applicable to the Angel Studios Messages at any time. If we
                    make changes, we will update this Section.
                  </p>
                  <p className="mt-4 font-semibold">
                    We are not responsible for incomplete, lost, late, or misdirected messages, including, without
                    limitation, undelivered messages resulting from any filtering by your mobile phone carrier. We
                    cannot guarantee that each user will receive the benefit of Angel Studios Messages for each Angel
                    Studios Message sent.
                  </p>
                  <p className="mt-4">
                    Without limiting the foregoing, by signing up to receive, or otherwise indicating that you want to
                    receive, Angel Studios Messages, you consent to the collection, use, and disclosure of your personal
                    information as described in our Privacy Policy, available at{' '}
                    <InternalLink
                      data-testid="privacy-policy-link"
                      className="underline"
                      href={paths.legal.privacyPolicy}
                    >
                      {paths.getBaseURL()}
                      {paths.legal.privacyPolicy}
                    </InternalLink>
                    .
                  </p>
                </li>
                <li className="mt-4">
                  <span className="font-semibold">Feedback.</span> By submitting ideas, suggestions, documents,
                  proposals, products, and/or technologies (“Ideas” or “Innovations”) to us in any way, you acknowledge
                  and agree that: (1) your Ideas and/or Innovations do not contain confidential or proprietary
                  information; (2) we are not under any obligation of confidentiality, express or implied, with respect
                  to the Ideas and Innovations; (3) we shall be entitled to disclose (or choose not to disclose) such
                  Ideas and Innovations for any purpose and in any way; (4) we may have something similar to the Ideas
                  and Innovations already under consideration or in development; (5) your Ideas and Innovations which
                  are not subject to a patent, automatically become our property without any obligation to you; and (6)
                  you are not entitled to any compensation or reimbursement of any kind from us under any circumstances.
                </li>
                <li className="mt-4">
                  <span className="font-semibold">Links to Third Party Websites.</span> Our Platform may include links
                  to other websites or services solely as a convenience to you. You acknowledge and agree that we are
                  not responsible for the availability of such external websites or services and that we do not endorse
                  and are not responsible or liable for any such linked websites or services or any information,
                  material, products, or services contained on or accessible through other websites or services.
                  Furthermore, we make no express or implied warranties with regard to the information, material,
                  products, or services that are contained on or accessible through linked websites or services. Your
                  access to and use of linked websites or services, including, without limitation, any information,
                  material, products and services on or accessible through other websites or services is solely at your
                  own risk.
                </li>
              </ol>
            </li>

            <li className="mt-4">
              <span className="font-semibold">TERMINATION</span>
              <ol className={orderedListRoman}>
                <li className="mt-4">
                  <span className="font-semibold">Right to Terminate.</span> Subject to the terms set forth in the
                  applicable Agreements, in addition to, and not in lieu of, any of our other rights set forth in these
                  Terms, we reserve the right, with or without notice and in our sole discretion, to terminate these
                  Terms, your account, and/or your ability to access or use our Platform for any reason, including,
                  without limitation, for lack of use or if we believe that you have violated or acted inconsistently
                  with the letter or spirit of these Terms, or in the case of any activity by you that may harm us or
                  other users, including, but not limited to, fraud, abuse of privileges, or misuse of our Platform. You
                  agree that we will not be liable to you or any third party for any such termination. You also have the
                  right to terminate your use of the Service, your Guild Membership, or any recurring Pay-It-Forward
                  transactions at any time without penalty.
                </li>
                <li className="mt-4">
                  <span className="font-semibold">Effects of Termination.</span> If we exercise our termination rights
                  available under the applicable Agreements, your license to access and use our Platform shall
                  immediately terminate and you must discontinue all access to and use of our Platform affected by such
                  termination. If you exercise your termination rights, we will stop charging your recurring Guild
                  Membership fees and/or Pay-It-Forward transactions as applicable. We will also stop fulfilling your
                  membership perks and delivery of other benefits and consideration, though you may still access the
                  free version of our Service.
                </li>
                <li className="mt-4">
                  <span className="font-semibold">Fraudulent Activity.</span> If we suspect that you are engaging in any
                  fraudulent, abusive, or illegal activity, we may refer such matter to appropriate law enforcement
                  authorities.
                </li>
                <li className="mt-4">
                  <span className="font-semibold">Survival.</span> The provisions of these Terms which by their nature
                  are intended to survive the termination or cancellation of these Terms shall continue as valid and
                  enforceable obligations notwithstanding any such termination or cancellation. Without limiting the
                  foregoing, the provisions of these Terms regarding indemnity and limitations of liability shall
                  survive the termination or cancellation of these Terms.
                </li>
              </ol>
            </li>

            <li className="mt-4">
              <span className="font-semibold">DISCLAIMERS</span>
              <ol className={orderedListRoman}>
                <li className="mt-4">
                  <span className="font-semibold">Disclaimer of all Warranties.</span>
                  <ol className={orderedListAlpha}>
                    <li className="mt-4">
                      OUR PLATFORM IS PROVIDED ON AN “AS IS” AND “AS AVAILABLE” BASIS AND WE EXPRESSLY DISCLAIM ALL
                      WARRANTIES OF ANY KIND RELATING TO OUR PLATFORM (INCLUDING, BUT NOT LIMITED TO, ANY SOFTWARE
                      COMPRISING ANY PORTION OF OUR PLATFORM), WHETHER EXPRESS OR IMPLIED, INCLUDING, BUT NOT LIMITED
                      TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY, TITLE, FITNESS FOR A PARTICULAR PURPOSE,
                      NON-INFRINGEMENT, OR THAT OUR PLATFORM WILL BE WITHOUT ERROR OR INVULNERABLE TO VIRUSES, WORMS OR
                      OTHER HARMFUL SOFTWARE OR HARDWARE.
                    </li>
                    <li className="mt-4">
                      YOU HEREBY ACKNOWLEDGE THAT OUR PLATFORM MAY NOT BE AVAILABLE DUE TO ANY NUMBER OF FACTORS
                      INCLUDING, BUT NOT LIMITED TO, PERIODIC SYSTEM MAINTENANCE, SCHEDULED OR UNSCHEDULED, ACTS OF GOD,
                      UNAUTHORIZED ACCESS, VIRUSES, DENIAL OF SERVICE OR OTHER ATTACKS, TECHNICAL FAILURE OF OUR
                      PLATFORM AND/OR TELECOMMUNICATIONS INFRASTRUCTURE, OR DISRUPTION, AND THEREFORE WE EXPRESSLY
                      DISCLAIM ANY EXPRESS OR IMPLIED WARRANTY REGARDING THE USE AND/OR AVAILABILITY, ACCESSIBILITY,
                      SECURITY, OR PERFORMANCE OF OUR PLATFORM CAUSED BY SUCH FACTORS.
                    </li>
                    <li className="mt-4">
                      WE DO NOT MAKE ANY REPRESENTATIONS OR WARRANTIES AGAINST THE POSSIBILITY OF DELETION, MISDELIVERY,
                      OR FAILURE TO STORE COMMUNICATIONS, PERSONALIZED SETTINGS, OR OTHER DATA, INCLUDING, WITHOUT
                      LIMITATION, ANY INFORMATION YOU SUBMIT THROUGH OUR PLATFORM.
                    </li>
                    <li className="mt-4">
                      WE ARE NOT RESPONSIBLE OR LIABLE FOR, NOR DO WE REPRESENT OR OTHERWISE WARRANT THE PERFORMANCE OF
                      ANY DEVICE YOU USE TO ACCESS OR USE OUR PLATFORM, INCLUDING, WITHOUT LIMITATION, THE CONTINUING
                      COMPATIBILITY OF ANY DEVICE WITH OUR PLATFORM. BY USING ANY DEVICE TO ACCESS OR USE OUR PLATFORM,
                      INCLUDING, WITHOUT LIMITATION, AN ANGEL STUDIOS SUPPORTED DEVICE TO ACCESS THE CONTENT, YOU AGREE
                      TO LOOK SOLELY TO THE ENTITY THAT MANUFACTURED AND/OR SOLD YOU THE DEVICE FOR ANY ISSUES RELATED
                      TO THE DEVICE AND YOUR ABILITY TO USE THE DEVICE TO ACCESS AND USE OUR PLATFORM.
                    </li>
                    <li className="mt-4">
                      SOME JURISDICTIONS DO NOT ALLOW THE EXCLUSION OF CERTAIN WARRANTIES. ACCORDINGLY, SOME OF THE
                      ABOVE DISCLAIMERS OF WARRANTIES MAY NOT APPLY TO YOU.
                    </li>
                  </ol>
                </li>
                <li className="mt-4">
                  <span className="font-semibold">Use of Platform at Your Sole Risk.</span> YOUR ACCESS TO AND USE OF
                  OUR PLATFORM, DOWNLOAD OF ANY SOFTWARE RELATING TO OUR PLATFORM, AND USE OF ANY INFORMATION WE MAY
                  PROVIDE IN CONNECTION WITH YOUR ACCESS TO OR USE OF OUR PLATFORM IS AT YOUR SOLE OPTION, DISCRETION,
                  AND RISK. WE SHALL NOT BE LIABLE FOR ANY MALFUNCTIONS OF THE SOFTWARE RELATING TO OUR PLATFORM, BUGS
                  OR VIRUSES RESULTING IN LOST DATA OR ANY OTHER DAMAGE TO YOUR COMPUTER EQUIPMENT, MOBILE PHONE OR
                  MOBILE DEVICE, OR SOFTWARE. FURTHERMORE, WE SHALL NOT BE LIABLE FOR ANY ATTEMPTS BY YOU TO ACCESS OR
                  USE OUR PLATFORM BY METHODS, MEANS OR WAYS NOT INTENDED BY US. WE ARE NOT REQUIRED TO PROVIDE
                  REDUNDANT OR BACKUP NETWORKS AND/OR SYSTEMS.
                  <ol className={orderedListAlpha}>
                    <li className="mt-4">
                      <span className="font-semibold">No Creation of Warranty.</span> NO ADVICE OR INFORMATION, WHETHER
                      ORAL OR WRITTEN OBTAINED BY YOU FROM ANY OF THE RELEASED PARTIES THROUGH OR FROM OUR PLATFORM
                      SHALL CREATE ANY WARRANTY NOT EXPRESSLY STATED IN THESE TERMS.
                    </li>
                    <li className="mt-4">
                      <span className="font-semibold">Third Party Statements.</span> WE DO NOT ENDORSE AND ARE NOT
                      RESPONSIBLE FOR THE STATEMENTS, ADVICE OR OPINIONS MADE BY ANYONE OTHER THAN AUTHORIZED ANGEL
                      STUDIOS SPOKESPERSONS. WE DO NOT ENDORSE AND ARE NOT RESPONSIBLE FOR ANY STATEMENTS, ADVICE OR
                      OPINIONS PROVIDED BY ANY THIRD PARTIES, AND SUCH STATEMENTS, ADVICE OR OPINIONS DO NOT IN ANY WAY
                      REFLECT THE STATEMENTS, ADVICE OR OPINIONS OF ANGEL STUDIOS.
                    </li>
                  </ol>
                </li>
              </ol>
            </li>
            <li className="mt-4">
              <span className="font-semibold">Refunds. </span>
              Thank you for shopping at Angel Studios. We strive to provide you with exceptional products and services.
              As part of our commitment to transparency and fairness, please review our refund policy outlined below:
              <ol className={orderedListRoman}>
                <li className="mt-4">
                  <span className="font-semibold">No Refunds.</span> We do not offer refunds on purchases unless the
                  product(s) received are defective or damaged upon arrival. In such cases, please contact our customer
                  service team within 15 days of receiving the product(s) to initiate the return process.
                </li>
                <li className="mt-4">
                  <span className="font-semibold">Defective or Damaged Products.</span> If you receive a defective or
                  damaged item, please contact us immediately with photographic evidence of the issue. Our customer
                  service team will assess the situation and provide instructions for returning the product(s) for an
                  exchange or store credit.
                </li>
                <li className="mt-4">
                  <span className="font-semibold">Exceptions</span> Certain products or circumstances may warrant
                  exceptions to this policy. We reserve the right to evaluate and make decisions on a case-by-case
                  basis. Items must be in resalable condition.
                </li>
                <li className="mt-4">
                  <span className="font-semibold">Non-Returnable Items</span> Although we agree to stop recurring
                  payments upon your termination of your Guild Membership or PIF subscription, the following items, once
                  charged, are categorically non-returnable: PIF contributions; movie tickets; Guild membership fees;
                  collectible scenes and frames; and other digital assets or goods.
                </li>
                <li className="mt-4">
                  <span className="font-semibold">Return Shipping</span> Customers are responsible for return shipping
                  costs unless otherwise specified. Process Time: Please allow at least 14 days for us to process your
                  return and issue an exchange or store credit upon receipt of the returned item(s).
                </li>
                <li className="mt-4">
                  <span className="font-semibold">Cancellation Policy</span> Orders cannot be canceled once they have
                  been processed and shipped. Please review your order carefully before completing the purchase.
                </li>
              </ol>
              <p className="mt-4">
                By making a purchase with Angel Studios, you acknowledge and agree to the terms of this refund policy.
              </p>
            </li>
            <li className="mt-4">
              <span className="font-semibold">YOUR LIABILITY</span>
              <ol className={orderedListRoman}>
                <li className="mt-4">
                  <span className="font-semibold">Agreement to Indemnify.</span> YOU AGREE THAT YOU WILL ONLY ACCESS AND
                  USE OUR PLATFORM IN ACCORDANCE WITH THE APPLICABLE AGREEMENTS. YOU WILL COMPENSATE US IN FULL FOR ANY
                  LOSSES OR COSTS (INCLUDING REASONABLE ATTORNEYS’ FEES) WHICH WE (OR ANY OF OUR SUBSIDIARIES OR
                  AFFILIATED COMPANIES) INCUR ARISING FROM ANY BREACH BY YOU OF THESE TERMS. WITHOUT LIMITING THE
                  GENERALITY OF THE FOREGOING, YOU AGREE TO INDEMNIFY, DEFEND, AND HOLD US, OUR SUBSIDIARIES AND
                  AFFILIATES AND OUR AND THEIR RESPECTIVE DIRECTORS, OFFICERS, EMPLOYEES, AGENTS, AND OTHER
                  REPRESENTATIVES (THE “RELEASED PARTIES”) HARMLESS FROM AND AGAINST ANY CLAIM OR DEMAND, INCLUDING
                  REASONABLE ATTORNEYS’ FEES, MADE BY ANY THIRD PARTY DUE TO OR ARISING OUT OF YOUR USE OF OUR PLATFORM,
                  YOUR CONNECTION TO OUR PLATFORM, YOUR VIOLATION OF THE APPLICABLE AGREEMENTS, OR YOUR VIOLATION OF ANY
                  INTELLECTUAL PROPERTY RIGHTS OF ANY OTHER PERSON OR ENTITY.
                </li>
                <li className="mt-4">
                  <span className="font-semibold">Additional Remedies.</span> The Released Parties reserve the right to
                  seek all remedies available at law and in equity for your violation of the applicable Agreements,
                  including the right to block access from a particular Internet address to our Platform and report
                  misuses to law enforcement.
                </li>
              </ol>
            </li>

            <li className="mt-4">
              <span className="font-semibold">LIMITATION OF LIABILITY</span>
              <ol className={orderedListRoman}>
                <li className="mt-4">
                  <span className="font-semibold">Maximum Liability.</span> EXCEPT AS OTHERWISE SET FORTH IN THE
                  APPLICABLE AGREEMENTS AND WITHOUT LIMITING ANYTHING SET FORTH IN THESE TERMS, TO THE FULLEST EXTENT
                  PERMITTED BY APPLICABLE LAW, OUR ENTIRE LIABILITY, AND YOUR EXCLUSIVE REMEDY, WITH RESPECT TO YOUR
                  ACCESS TO AND USE OF OUR PLATFORM SHALL BE THE AMOUNT OF $100.
                </li>
                <li className="mt-4">
                  <span className="font-semibold">No Liability for Incidental Damages.</span> IN NO EVENT WILL WE BE
                  LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, PUNITIVE, OR CONSEQUENTIAL DAMAGES ARISING
                  FROM YOUR ACCESS TO OR USE OF OUR PLATFORM OR FOR ANY OTHER CLAIM RELATED IN ANY WAY TO YOUR ACCESS TO
                  OR USE OF OUR PLATFORM.
                </li>
                <li className="mt-4">
                  <span className="font-semibold">Applicability of Limitations.</span> THE FOREGOING LIMITATIONS WILL
                  APPLY WHETHER SUCH DAMAGES ARISE OUT OF BREACH OF CONTRACT, TORT (INCLUDING NEGLIGENCE), OR OTHERWISE
                  AND REGARDLESS OF WHETHER SUCH DAMAGES WERE FORESEEABLE OR WE WERE ADVISED OF THE POSSIBILITY OF SUCH
                  DAMAGES. SOME STATES OR JURISDICTIONS DO NOT ALLOW CERTAIN LIMITATIONS OF LIABILITY, SO SOME OF THE
                  ABOVE LIMITATIONS OF LIABILITY MAY NOT APPLY TO YOU. IN SUCH STATES OR JURISDICTIONS, OUR LIABILITY
                  WILL BE LIMITED TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW.
                </li>
                <li className="mt-4">
                  <span className="font-semibold">Notice to California Residents.</span> IF YOU ARE A CALIFORNIA
                  RESIDENT, YOU HEREBY WAIVE CALIFORNIA CIVIL CODE SECTION 1542 IN CONNECTION WITH THE FOREGOING, WHICH
                  STATES “A GENERAL RELEASE DOES NOT EXTEND TO CLAIMS WHICH THE CREDITOR DOES NOT KNOW OR SUSPECT TO
                  EXIST IN HIS OR HER FAVOR AT THE TIME OF EXECUTING THE RELEASE, WHICH IF KNOWN BY HIM OR HER MUST HAVE
                  MATERIALLY AFFECTED HIS OR HER SETTLEMENT WITH THE DEBTOR.”
                </li>
              </ol>
            </li>

            <li className="mt-4">
              <span className="font-semibold">DISPUTE RESOLUTION</span>
              <p className="mt-4 font-semibold">PLEASE READ THIS SECTION CAREFULLY. IT AFFECTS YOUR RIGHTS.</p>
              <ol className={orderedListRoman}>
                <li className="mt-4">
                  <span className="font-semibold">Waiver of Rights.</span> YOU AGREE THAT BY ACCESSING OR USING OUR
                  SERVICES, OR BECOMING A GUILD MEMBER OR PIF SUBSCRIBER, YOU ARE WAIVING THE RIGHT TO A COURT OR JURY
                  TRIAL OR TO PARTICIPATE IN A CLASS ACTION. YOU AGREE THAT YOU MAY BRING CLAIMS AGAINST THE INDEMNIFIED
                  PARTIES ONLY IN YOUR INDIVIDUAL CAPACITY AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED
                  CLASS, REPRESENTATIVE OR COLLECTIVE PROCEEDING. ANY ARBITRATION WILL TAKE PLACE ON AN INDIVIDUAL
                  BASIS. CLASS ARBITRATIONS AND CLASS ACTIONS ARE NOT PERMITTED.
                </li>
                <li className="mt-4">
                  <span className="font-semibold">Agreement to Arbitrate.</span>
                  <ol className={orderedListAlpha}>
                    <li className="mt-4">
                      YOU AGREE THAT ANY AND ALL CLAIMS AND DISPUTES ARISING FROM OR RELATING IN ANY WAY TO THESE TERMS,
                      YOUR ACCESS TO OR USE OF THE SERVICES, YOUR GUILD MEMBERSHIP, YOUR PIF SUBSCRIPTION, OR YOUR
                      DEALINGS WITH THE INDEMNIFIED PARTIES IN CONNECTION WITH THE FOREGOING SHALL BE FINALLY SETTLED
                      AND RESOLVED THROUGH BINDING, INDIVIDUAL ARBITRATION AS DESCRIBED IN THIS SECTION.
                    </li>
                    <li className="mt-4">
                      THIS AGREEMENT TO ARBITRATE INVOLVES INTERSTATE COMMERCE, AND, THEREFORE, SHALL BE GOVERNED BY THE
                      FEDERAL ARBITRATION ACT, 9 U.S.C. 1-16 (“FAA”), AND NOT BY STATE LAW.
                    </li>
                    <li className="mt-4">
                      THIS AGREEMENT TO ARBITRATE IS INTENDED TO BE INTERPRETED BROADLY PURSUANT TO THE FAA.
                    </li>
                    <li className="mt-4">
                      THE ARBITRATION WILL BE GOVERNED BY THE COMMERCIAL ARBITRATION RULES AND THE SUPPLEMENTARY
                      PROCEDURES FOR CONSUMER RELATED DISPUTES OF THE AMERICAN ARBITRATION ASSOCIATION (“AAA”), AS
                      MODIFIED BY THIS SECTION.
                    </li>
                    <li className="mt-4">
                      FOR ANY CLAIM WHERE THE TOTAL AMOUNT OF THE AWARD SOUGHT IS $10,000 OR LESS, YOU MUST ABIDE BY THE
                      FOLLOWING RULES: (1) THE ARBITRATION SHALL BE CONDUCTED SOLELY BASED ON TELEPHONE OR ONLINE
                      APPEARANCES AND/OR WRITTEN SUBMISSIONS AND (2) THE ARBITRATION SHALL NOT INVOLVE ANY PERSONAL
                      APPEARANCE BY THE PARTIES OR WITNESSES UNLESS OTHERWISE MUTUALLY AGREED BY THE PARTIES. IF THE
                      CLAIM EXCEEDS $10,000, THE RIGHT TO A HEARING WILL BE DETERMINED BY THE AAA RULES, AND THE HEARING
                      (IF ANY) WILL TAKE PLACE IN UTAH COUNTY, UTAH.
                    </li>
                    <li className="mt-4">
                      THE ARBITRATOR IS BOUND BY THE TERMS OF THIS AGREEMENT TO ARBITRATE. YOU ACKNOWLEDGE AND AGREE
                      THAT, IN ANY ARBITRATION PROCEEDING, NO DEPOSITIONS WILL BE TAKEN, AND ALL OTHER FORMS OF
                      DISCOVERY OF FACTS WILL BE LIMITED TO THOSE THINGS THAT THE ARBITRATOR DETERMINES, IN ITS SOLE
                      DISCRETION, TO BE NECESSARY. FURTHER, IN ANY ARBITRATION PROCEEDING: (1) THERE SHALL BE NO
                      PUNITIVE, EXEMPLARY, INCIDENTAL, OR CONSEQUENTIAL OR OTHER SPECIAL DAMAGES; (2) ALL DAMAGES,
                      CLAIMS, AND AWARDS WILL BE GOVERNED BY UTAH LAW; (3) THE PARTIES WILL CONDUCT THE ARBITRATION
                      CONFIDENTIALLY AND EXPEDITIOUSLY AND WILL PAY THEIR OWN COSTS AND EXPENSES OF ARBITRATION,
                      INCLUDING THEIR OWN ATTORNEY’S FEES. IF YOU PROVE THAT YOU ARE UNABLE TO AFFORD THE AAA FEE, YOU
                      AGREE TO NOTIFY ALL PERSONS AGAINST WHOM YOU HAVE AN ARBITRABLE CLAIM AND GIVE SUCH PERSONS THE
                      OPPORTUNITY, INDIVIDUALLY AND AS A GROUP, TO PAY SUCH FEE. THE PROCEEDING AND THE DECISION SHALL
                      BE KEPT CONFIDENTIAL BY THE PARTIES.
                    </li>
                    <li className="mt-4">
                      THE ARBITRATOR MAY AWARD DECLARATORY OR INJUNCTIVE RELIEF ONLY IN FAVOR OF THE INDIVIDUAL PARTY
                      SEEKING RELIEF AND ONLY TO THE EXTENT NECESSARY TO PROVIDE RELIEF WARRANTED BY THAT PARTY’S
                      INDIVIDUAL CLAIM.
                    </li>
                    <li className="mt-4">
                      THE ARBITRATOR’S RULING IS BINDING AND MAY BE ENTERED AS A JUDGMENT IN ANY COURT OF COMPETENT
                      JURISDICTION, OR APPLICATION MAY BE MADE TO SUCH COURT FOR JUDICIAL ACCEPTANCE OF ANY AWARD AND AN
                      ORDER OF ENFORCEMENT, AS THE CASE MAY BE.
                    </li>
                    <li className="mt-4">
                      EXCEPT IN THE EVENT YOU OPT OUT OF THIS AGREEMENT TO ARBITRATE IN ACCORDANCE WITH SECTION 11.5,
                      THIS AGREEMENT TO ARBITRATE WILL SURVIVE TERMINATION OF THESE TERMS AND/OR YOUR ACCESS TO OR USE
                      OF THE SERVICES AND YOUR RELATIONSHIP WITH THE INDEMNIFIED PARTIES IN CONNECTION THEREWITH.
                    </li>
                    <li className="mt-4">
                      ALL CHALLENGES TO THE VALIDITY AND APPLICABILITY OF THIS AGREEMENT TO ARBITRATE (I.E., WHETHER A
                      PARTICULAR CLAIM OR DISPUTE IS SUBJECT TO ARBITRATION) SHALL BE DETERMINED BY THE ARBITRATOR.
                    </li>
                  </ol>
                </li>
                <li className="mt-4">
                  <span className="font-semibold">Information About Arbitration.</span> INFORMATION ON AAA AND HOW TO
                  START ARBITRATION CAN BE FOUND AT{' '}
                  <ExternalLink data-testid="adr-link" className="underline" href="https://www.adr.org">
                    WWW.ADR.ORG
                  </ExternalLink>
                  . THERE IS NO JUDGE OR JURY IN ARBITRATION. ARBITRATION PROCEDURES ARE SIMPLER AND MORE LIMITED THAN
                  RULES APPLICABLE IN COURT AND REVIEW BY A COURT IS LIMITED. YOU WILL NOT BE ABLE TO HAVE A COURT OR
                  JURY TRIAL OR PARTICIPATE IN A CLASS ACTION OR CLASS ARBITRATION. YOU UNDERSTAND AND AGREE THAT BY
                  AGREEING TO RESOLVE ANY DISPUTE THROUGH INDIVIDUAL ARBITRATION, YOU ARE WAIVING THE RIGHT TO A COURT
                  OR JURY TRIAL. ANY DISPUTE SHALL BE ARBITRATED ON AN INDIVIDUAL BASIS, AND NOT AS A CLASS ACTION,
                  REPRESENTATIVE ACTION, CLASS ARBITRATION, OR SIMILAR PROCEEDING. THE ARBITRATOR MAY NOT CONSOLIDATE
                  THE CLAIMS OF MULTIPLE PARTIES.
                </li>
                <li className="mt-4">
                  <span className="font-semibold">Challenges to Validity of Agreement to Arbitrate.</span> INFORMATION
                  ON AAA AND HOW TO START ARBITRATION CAN BE FOUND AT{' '}
                  <ExternalLink data-testid="adr-link" className="underline" href="https://www.adr.org">
                    WWW.ADR.ORG
                  </ExternalLink>
                  . THERE IS NO JUDGE OR JURY IN ARBITRATION. ARBITRATION PROCEDURES ARE SIMPLER AND MORE LIMITED THAN
                  RULES APPLICABLE IN COURT AND REVIEW BY A COURT IS LIMITED. YOU WILL NOT BE ABLE TO HAVE A COURT OR
                  JURY TRIAL OR PARTICIPATE IN A CLASS ACTION OR CLASS ARBITRATION. YOU UNDERSTAND AND AGREE THAT BY
                  AGREEING TO RESOLVE ANY DISPUTE THROUGH INDIVIDUAL ARBITRATION, YOU ARE WAIVING THE RIGHT TO A COURT
                  OR JURY TRIAL. ANY DISPUTE SHALL BE ARBITRATED ON AN INDIVIDUAL BASIS, AND NOT AS A CLASS ACTION,
                  REPRESENTATIVE ACTION, CLASS ARBITRATION, OR SIMILAR PROCEEDING. THE ARBITRATOR MAY NOT CONSOLIDATE
                  THE CLAIMS OF MULTIPLE PARTIES.
                </li>
                <li className="mt-4">
                  <span className="font-semibold">Opt-Out of Agreement to Arbitrate.</span> IF YOU WISH TO OPT-OUT OF
                  THE AGREEMENT TO ARBITRATE, WITHIN 45 DAYS OF APRIL 5TH, 2022 OR WHEN YOU FIRST ACCESS OR USE OUR
                  SERVICES, WHICHEVER IS LATER, YOU MUST SEND US A LETTER STATING: “REQUEST TO OPT-OUT OF AGREEMENT TO
                  ARBITRATE” TO:
                  <div className="mt-4">
                    <p>Angel Studios Inc.</p>
                    <p>295 W Center St Suite A</p>
                    <p>Provo, UT 84601</p>
                  </div>
                </li>
                <li className="mt-4">
                  <span className="font-semibold">Venue for Litigation.</span> IF THE AGREEMENT TO ARBITRATE IN SECTION
                  11.2 ABOVE IS FOUND UNENFORCEABLE OR TO NOT APPLY FOR A GIVEN DISPUTE, OR IF YOU OPT OUT OF THE
                  AGREEMENT TO ARBITRATE IN ACCORDANCE WITH SECTION 11.5, THEN, UNLESS PROHIBITED BY APPLICABLE LAW, THE
                  PROCEEDINGS MUST BE BROUGHT EXCLUSIVELY IN THE STATE AND FEDERAL COURTS COVERING UTAH COUNTY, UTAH, AS
                  APPROPRIATE. YOU ALSO THEREFORE AGREE TO SUBMIT TO THE PERSONAL JURISDICTION OF EACH OF THESE COURTS
                  FOR THE PURPOSES OF LITIGATING SUCH CLAIMS OR DISPUTES, AND YOU HEREBY WAIVE YOUR RIGHT TO A JURY
                  TRIAL, WAIVE YOUR RIGHT TO INITIATE OR PARTICIPATE IN A CLASS OR COLLECTIVE ACTION, AND AGREE TO
                  REMAIN BOUND BY ANY AND ALL LIMITATIONS OF LIABILITY AND DAMAGES INCLUDED IN THIS AGREEMENT.
                </li>
                <li className="mt-4">
                  <span className="font-semibold">Time to Bring Claim.</span> ANY CAUSE OF ACTION OR CLAIM YOU MAY HAVE
                  ARISING OUT OF OR RELATING IN ANY WAY TO THE AGREEMENTS, YOUR ACCESS TO OR USE OF THE PLATFORM, OR
                  YOUR DEALINGS WITH THE RELEASED PARTIES MUST BE COMMENCED IN ARBITRATION OR LITIGATION (AS APPLICABLE)
                  WITHIN TWO (2) YEARS AFTER THE CAUSE OF ACTION ACCRUES. AFTER THAT TWO (2) YEAR PERIOD, SUCH CAUSE OF
                  ACTION OR CLAIMS IS PERMANENTLY BARRED. SOME JURISDICTIONS DO NOT ALLOW TIME LIMITATIONS OTHER THAN
                  THOSE SET FORTH IN SUCH STATE’S STATUTE OF LIMITATIONS. IN SUCH CASES, THE APPLICABLE STATUTE OF
                  LIMITATIONS PROVIDED FOR UNDER THE LAWS OF SUCH STATE SHALL APPLY.
                </li>
                <li className="mt-4">
                  <span className="font-semibold">Prevailing Party.</span> IN ANY ARBITRATION PROCEEDING OR ANY LEGAL
                  SUIT, ACTION, OR PROCEEDING (AS APPLICABLE) BETWEEN YOU AND THE INDEMNIFIED PARTIES RELATED TO THESE
                  TERMS, YOUR ACCESS TO OR USE OF THE SERVICES, OR YOUR DEALINGS WITH THE INDEMNIFIED PARTIES IN
                  CONNECTION THEREWITH, THE PREVAILING PARTY SHALL HAVE THE RIGHT TO COLLECT FROM THE NON-PREVAILING
                  PARTY ITS REASONABLE LEGAL FEES AND COSTS, INCLUDING, WITHOUT LIMITATION, ATTORNEY’S FEES, EXPENSES,
                  AND COURT COSTS INCURRED IN CONNECTION THEREWITH.
                </li>
              </ol>
            </li>

            <li className="mt-4">
              <span className="font-semibold">NOTICE AND PROCEDURE FOR MAKING CLAIMS OF COPYRIGHT INFRINGEMENT</span>
              <ol className={orderedListRoman}>
                <li className="mt-4">
                  <span className="font-semibold">Angel Studios Policy.</span> Angel Studios respects the intellectual
                  property rights of others. You must ensure that Your Content does not infringe any third party’s
                  copyright. We will remove Your Content and other materials on our Platform in accordance with the
                  Digital Millennium Copyright Act (“DMCA”) upon receipt of proper notices that Your Content or other
                  materials on our Platform infringe a third party’s copyright. Additionally, subject to Section 12.4
                  below, we will terminate your account if you are a repeat infringer.
                </li>
                <li className="mt-4">
                  <span className="font-semibold">Notice of Infringement.</span> If you are a copyright owner or an
                  agent thereof and believe that any user submission or other content on our Platform infringes upon
                  your copyrights, you may submit a notification pursuant to the DMCA to the address provided below.
                  Such notification must contain the following:
                  <ol className={orderedListAlpha}>
                    <li className="mt-4">
                      a physical or electronic signature of a person authorized to act on behalf of the owner of an
                      exclusive right that is allegedly infringed;
                    </li>
                    <li className="mt-4">
                      identification of the copyrighted work claimed to have been infringed, or, if multiple copyrighted
                      works at a single online site are covered by a single notification, a representative list of such
                      works at that site;
                    </li>
                    <li className="mt-4">
                      identification of the material that is claimed to be infringing or to be the subject of infringing
                      activity and that is to be removed or access to which is to be disabled and information reasonably
                      sufficient to permit us to locate the material;
                    </li>
                    <li className="mt-4">
                      information reasonably sufficient to permit us to contact you, such as an address, telephone
                      number, and, if available, an electronic mail address;
                    </li>
                    <li className="mt-4">
                      a statement that you have a good faith belief that use of the material in the manner complained of
                      is not authorized by the copyright owner, its agent, or the law; and
                    </li>
                    <li className="mt-4">
                      a statement that the information in the notification is accurate and, under penalty of perjury,
                      that you are authorized to act on behalf of the owner of an exclusive right that is allegedly
                      infringed.
                    </li>
                  </ol>
                  <p className="mt-4">
                    Angel Studios may disclose DMCA notices to affected users and third party databases that collect
                    information about copyright takedown notices.
                  </p>
                </li>
                <li className="mt-4">
                  <span className="font-semibold">Counter Notifications.</span> If Your Content is removed pursuant to a
                  notice of copyright infringement and you want to challenge the removal, you must provide us a counter
                  notification to the address provided below. Such counter-notification must contain the following:
                  <ol className={orderedListAlpha}>
                    <li className="mt-4">
                      information reasonably sufficient to permit us to contact you, such as an address, telephone
                      number, and, if available, an electronic mail address;
                    </li>
                    <li className="mt-4">
                      a description of the material that was removed and where the material previously appeared on our
                      Platform reasonably sufficient to permit us to identify the material;
                    </li>
                    <li className="mt-4">
                      a statement, under penalty of perjury, that you have a good faith belief that the material was
                      removed or disabled as a result of mistake or misidentification;
                    </li>
                    <li className="mt-4">
                      a statement that you consent to the jurisdiction of the Federal District Court for the judicial
                      district in which your address is located, or, if your address is outside the United States, any
                      judicial district in which Angel Studios may be found (the United States District Court for the
                      District of Utah) and that you will accept service of process from the person who provided the
                      original DMCA notice or an agent of that person; and
                    </li>
                    <li className="mt-4">your physical or electronic signature.</li>
                  </ol>
                  <p className="mt-4">
                    We will forward any complete counter-notification to the person who provided the initial DMCA
                    notice. The copyright owner(s) may elect to file a lawsuit against you for copyright infringement.
                    If we do not receive notice that such a lawsuit has been filed within ten (10) business days after
                    we provide notice of your counter-notification, we may, but are not obligated to, restore Your
                    Content and other materials. Until that time, Your Content and other materials will remain removed.
                  </p>
                </li>
                <li className="mt-4">
                  <span className="font-semibold">Address for Notices.</span> DMCA notices and counter-notifications may
                  be sent to our designated agent, JUDD FUNK at the following addresses:
                  <p className="mt-4">
                    By email to:{' '}
                    <a data-testid="mail-to-link" className="underline" href="mailto:legal@angel.com">
                      legal@angel.com
                    </a>
                  </p>
                  <p className="mt-4">By mail to:</p>
                  <div className="mt-4">
                    <p>Angel Studios Inc.</p>
                    <p>Attn: Angel Legal Dept.</p>
                    <p>295 W Center St Suite A</p>
                    <p>Provo, UT 84601</p>
                  </div>
                </li>
              </ol>
            </li>

            <li className="mt-4">
              <span className="font-semibold">ADDITIONAL TERMS</span>
              <ol className={orderedListRoman}>
                <li className="mt-4">
                  <span className="font-semibold">Governing Law.</span> These Terms will be governed by the laws of
                  Utah, without giving effect to any principles of conflicts of laws.
                </li>
                <li className="mt-4">
                  <span className="font-semibold">Electronic Communications.</span> The communications between you and
                  Angel Studios use electronic means, whether you access or use our Platform or send us emails, or
                  whether we post notices on our Platform or communicate with you via email. For contractual purposes,
                  you: (1) consent to receive communications from us in an electronic form; and (2) agree that all terms
                  and conditions, agreements, notices, disclosures, and other communications that we provide to you
                  electronically, including, but not limited to, the Rules, satisfy any legal requirement that such
                  communications would satisfy if they were provided in a hardcopy writing. The foregoing does not
                  affect your non-waivable rights.
                </li>
                <li className="mt-4">
                  <span className="font-semibold">Severability.</span> If any part of these Terms are determined to be
                  invalid or unenforceable pursuant to applicable law, including, but not limited to, any warranty
                  disclaimers and limitations of liability set forth above, then the invalid or unenforceable provision
                  will be deemed superseded by a valid, enforceable provision that most closely matches the intent of
                  the original provision and the remainder of the applicable Terms shall continue in effect.
                </li>
                <li className="mt-4">
                  <span className="font-semibold">Waivers.</span> Our failure to enforce any provision of these Terms
                  shall not be deemed a waiver of such provision nor of the right to enforce such provision.
                </li>
                <li className="mt-4">
                  <span className="font-semibold">Admissibility of Terms.</span> A printed version of these Terms and of
                  any notice given in electronic form shall be admissible in arbitration, judicial, or administrative
                  proceedings based upon or relating to these Terms to the same extent and subject to the same
                  conditions as other business documents and records originally generated and maintained in printed
                  form.
                </li>
                <li className="mt-4">
                  <span className="font-semibold">Assignment.</span> We reserve the right to transfer, assign,
                  sublicense, or pledge these Terms, in whole or in part, to any person without notice, provided that
                  any such assignment will be on the same terms or terms that are no less advantageous to you. You may
                  not assign, sublicense, or otherwise transfer in any manner whatsoever any of your rights or
                  obligations under these Terms.
                </li>
                <li className="mt-4">
                  <span className="font-semibold">Headings.</span> The section headings and sub-headings contained in
                  these Terms are for convenience only and have no legal or contractual effect.
                </li>
                <li className="mt-4">
                  <span className="font-semibold">Support.</span> If you have any questions or concerns about our
                  Platform or these Terms, please email us at{' '}
                  <a data-testid="support-link" className="underline" href="mailto:support@angel.com">
                    support@angel.com
                  </a>
                  .
                </li>
              </ol>
            </li>
          </ol>
        </div>
      </PaddedContainer>
    </div>
  )
}
